import { useState } from 'react';

import { R } from '@/features/profile/resources/ProfileResources.resx';
import { format, R as GlobalResources } from '@/Resources.resx';
import { Utils } from '@/shared/Utils';
import { fetch } from '@/shared/fetch';
import {
    ButtonWithIcon,
    HorizontalContainer,
    VerticalContainer,
} from '@/components';
import { InfoIcon, TrashIcon } from '@/components/Icons';
import { DeleteAccountModal } from '@/features/profile/widgets/delete-account/ui/DeleteAccountModal';

export interface DeleteAccountProps {
    showSubscriptionWarning: boolean;
    showAdolescentWarning: boolean;
    adolescentMinimumAge: number;
    adultMinimumAge: number;
}

export function DeleteAccount({
    showSubscriptionWarning,
    showAdolescentWarning,
    adolescentMinimumAge,
    adultMinimumAge,
}: DeleteAccountProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const openDeleteAccountModal = () => {
        setError(null);
        setIsModalOpen(true);
    };

    const closeDeleteAccountModal = () => {
        if (isDeleting) return;

        setIsModalOpen(false);
    };

    const deleteAccount = async () => {
        if (isDeleting) return;

        setIsDeleting(true);
        setError(null);

        try {
            await fetch('/api/patients/account', 'DELETE');

            Utils.logout();
        } catch {
            setError(GlobalResources.ErrorDeleteAccount);
            setIsDeleting(false);
        }
    };

    const [beforeEmailWarning, afterEmailWarning] =
        R.SubscriptionWarningText.split('{0}');

    const [beforeEmail, afterEmail] =
        R.DeleteAccountSubDescription2.split('{0}');

    return (
        <VerticalContainer spacing="md" className="h-full justify-between">
            <h2 id="heading-DeleteAccount" className="max-w-prose">
                {R.DeleteAccount}
            </h2>
            <div className="max-w-prose">{R.DeleteDescription}</div>

            {showSubscriptionWarning && (
                <HorizontalContainer className="items-center rounded-2xl border border-(--color-border-danger) bg-(--color-bg-danger) p-4">
                    <InfoIcon variant="error" className="shrink-0" />
                    <div className="flex flex-col">
                        <div className="mb-1 font-bold text-(--color-text-danger)">
                            {R.SubscriptionWarningTitle}
                        </div>
                        <div className="text-(--color-text-danger)">
                            {beforeEmailWarning}
                            <a
                                href={`mailto:${R.ContactEmail}`}
                                className="font-bold text-(--color-text-danger)"
                            >
                                {R.ContactEmail}
                            </a>
                            {afterEmailWarning}
                        </div>
                    </div>
                </HorizontalContainer>
            )}

            {showAdolescentWarning && (
                <HorizontalContainer className="items-center rounded-2xl border border-(--color-border-danger) bg-(--color-bg-danger) p-4">
                    <InfoIcon className="shrink-0" variant="error" />
                    <div className="flex flex-col">
                        <div className="mb-1 font-bold text-(--color-text-danger)">
                            {format(
                                R.AdolescentWarningTitle,
                                adolescentMinimumAge.toString(),
                                (adultMinimumAge - 1).toString(),
                            )}
                        </div>
                        <div className="text-(--color-text-danger)">
                            {format(
                                R.AdolescentWarningText,
                                adolescentMinimumAge.toString(),
                                (adultMinimumAge - 1).toString(),
                            )}
                        </div>
                    </div>
                </HorizontalContainer>
            )}

            <VerticalContainer spacing="none">
                <h5>{R.DeleteAccountSubHeader1}</h5>
                <div>{R.DeleteAccountSubDescription1}</div>
            </VerticalContainer>

            <VerticalContainer spacing="none">
                <h5>{R.DeleteAccountSubHeader2}</h5>
                <div>
                    {beforeEmail}
                    <a
                        href={`mailto:${R.ContactEmail}`}
                        className="text-(--color-text-link)"
                    >
                        {R.ContactEmail}
                    </a>
                    {afterEmail}
                </div>
            </VerticalContainer>

            <ButtonWithIcon variant="danger" onClick={openDeleteAccountModal}>
                <TrashIcon />
                {R.DeleteAccountButton}
            </ButtonWithIcon>

            {isModalOpen && (
                <DeleteAccountModal
                    closeDeleteAccountModal={closeDeleteAccountModal}
                    deleteAccount={deleteAccount}
                    isDeleting={isDeleting}
                    error={error}
                />
            )}
        </VerticalContainer>
    );
}
